import { useContext, useMemo, createElement, Children, useCallback } from 'react';
import { useTheme } from 'styled-components';
import { metadata, constants } from 'lib_ui-services';
import {
    contexts,
    Text,
    View,
    MenuShellItem,
    styled,
    testProperties,
    fromTheme,
    nativeOnlyStyles
} from 'lib_ui-primitives';
import HNode from '../../HNode';
import DropDownButton from '../_abstractComponent/DropDownButton';
import LoadingBoundary from '../contextProviders/LoadingBoundary';
import useBrand from '../../hooks/useBrand';
import useHasFeatureFlag from '../../hooks/useHasFeatureFlag';
import ThemeSelector from '../Theme/ThemeSelector';
import TenantSwitcherMenuItem from './customMenus/TenantSwitcherMenuItem';
import Toggle from '../_abstractComponent/Toggle';
import { MenuShell } from '../menuSelection';

// For some reason, the Dark Mode switch will be dropped if you put this in the
// import above (e.g. import {createElement as rc} from 'react').
// We couldn't figure out why -- thinking maybe webpack does something weird here?
const rc = createElement;

const SmallDebugInfo = styled(View).attrs({ name: 'small-debug-info' })`
    flex-direction: column;
`;
SmallDebugInfo.displayName = 'SmallDebugInfo';
let VersionContainer = styled(View).attrs({ name: 'version-container' })`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: 8px;
    max-width: ${fromTheme('menu', 'native', 'versionFooter', 'maxWidth')};
`;
VersionContainer.displayName = 'VersionContainer';
VersionContainer = nativeOnlyStyles(VersionContainer)`
    margin-left: ${fromTheme('menu', 'native', 'versionFooter', 'marginLeft')};
    margin-top: ${fromTheme('menu', 'native', 'versionFooter', 'marginTop')};
`;

const ProfileStyle = styled(View)`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
`;

const Version = styled(Text).attrs({ name: 'version' })`
    font-size: 8px;
    color: ${fromTheme('menu', 'footer', 'color')};

    ${({ theme, textWeight, textSize }) => fromTheme('fonts', textSize, textWeight)({ theme })}
`;

const PaddedDarkModeSwitch = styled(View).attrs({ name: 'padded-dark-mode-switch' })`
    padding: 16px;
`;
PaddedDarkModeSwitch.displayName = 'PaddedDarkModeSwitch';

const _p = {
    useBrand,
    useHasFeatureFlag,
    getUseCaseTitle: metadata.getUseCaseTitle,
    getUseCaseId: metadata.getUseCaseId
};
export const _private = _p;

/**
 * THIS "MENU" IS ACTUALLY HARDCODED TO BE THE PROFILE MENU!!!
 * NOT A GENERIC MENU COMPONENT!!!
 * @param {object} props
 * @param {object} props.hNode
 * @param {boolean} [props.hNode.showDarkModeToggle]
 * @param {string} [props.hNode.buttonSize]
 * @param {object} props.hNode.children
 * @param {React.ReactNode} props.children
 * @returns {React.Component|null}
 */
export default function Menu(props) {
    const { children, hNode } = props || {};
    const { darkMode, setDarkMode, selectedTheme, titleBar } = useTheme();

    //*************************************************************************
    // Dark Mode related settings - this was originally implemented as a metadata
    // action toggle, but this renders too early, router is not ready yet, and
    // that causes useBbState (consumed by action toggle) to create a circular
    // dependency loop.
    const { showDarkModeToggle = false, buttonSize = titleBar.buttonSize } = hNode;
    const onClick = useCallback(checked => setDarkMode(checked), [setDarkMode]);
    //*************************************************************************

    const session = useContext(contexts.SessionContext);
    if (session.role == null) {
        throw new Error('A login session exists, but no role was found for the user profile.');
    }
    const isSupport = session?.role?._id === constants.WELL_KNOWN_ROLE_IDS.SUPPORT;
    const release = metadata.getUseCaseRelease();
    const webPageBrand = _p.useBrand();
    const defaultToGl2025 = _p.useHasFeatureFlag('gl2025');
    const useCaseTitle = useMemo(() => _p.getUseCaseTitle(webPageBrand), [webPageBrand]);

    // Filter to appropriate Menu components
    const [menuItems, logout] = useMemo(() => {
        let logout = null;
        const children = (hNode.children || []).map(hNode => {
            if (hNode.hNodeTypeGroup === 'menuSelection') {
                if (hNode.hNodeType === 'MenuItem' && hNode.forAction === 'logout') {
                    logout = rc(HNode, { hNode });
                    return null;
                }
                return rc(HNode, { hNode });
            } else {
                return null;
            }
        });
        return [children, logout];
    }, [hNode.children]);

    const otherItems = useMemo(() => {
        const c = Children.toArray(children).filter(
            component => component.props?.hNode?.hNodeTypeGroup !== 'menuSelection'
        );
        return c;
    }, [children]);

    const icon = useMemo(() => {
        return selectedTheme === 'gl2025' ? 'user' : 'person';
    }, [selectedTheme]);

    // prettier-ignore
    return rc(ProfileStyle, null,
        rc(LoadingBoundary, null,
            rc(DropDownButton, {
                    icon,
                    id: props.id || 'profile',
                    variant: 'secondary', 
                    size: buttonSize,
                    ...testProperties(hNode, 'profile')
                },
                ...menuItems,
                (defaultToGl2025 || isSupport) && rc(ThemeSelector),
                // Dark mode toggle (see above for more info)
                showDarkModeToggle && rc(MenuShell, {hNode:{iconName:'moon'}},
                    rc(Toggle, {
                        onClick,
                        title: 'Dark Mode',
                        value: darkMode,
                        falseLabel: '',
                        trueLabel: '',
                        id: 'darkMode' + props.id
                    })
                ),
                rc(DropDownButton.MenuDivider),
                // Allows switching between tenants without logging out
                rc(TenantSwitcherMenuItem),
                logout,
                rc(DropDownButton.MenuDivider),
                rc(MenuShellItem, { key: 'miVersion' },
                    rc(VersionContainer, null,
                        rc(Version, {textSize: 'xs', textWeight: 'regular'}, `Platform: v${__PACKAGE_VERSION__}`),
                        rc(Version, {textSize: 'xs', textWeight: 'regular'}, `${useCaseTitle}: v${release}`)
                    )
                )
            ),
            ...otherItems
        )
    );
}
